"use client";

import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Key, Eye, EyeOff } from "lucide-react";
import { useChangePassword } from "@/hooks/api/use-auth";
import { useNotification } from "@/hooks/use-notification-v2";

interface ChangePasswordSimpleModalProps {
  visible: boolean;
  onHide: () => void;
}

export function ChangePasswordSimpleModal({ visible, onHide }: ChangePasswordSimpleModalProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const { showToast } = useNotification();
  const changePasswordMutation = useChangePassword({
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      showToast("Senha alterada com sucesso!", "success");
      onHide();
    },
    onError: (error: string) => {
      showToast(error, "error");
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword.trim()) {
      showToast("Por favor, informe a senha atual", "warning");
      return;
    }

    if (newPassword.length < 8) {
      showToast("A nova senha deve ter pelo menos 8 caracteres", "warning");
      return;
    }

    if (currentPassword === newPassword) {
      showToast("A nova senha deve ser diferente da senha atual", "warning");
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword,
        newPassword,
      });
    } catch (error: unknown) {
      // Error handling is done in the mutation callbacks
    }
  };

  const handleHide = () => {
    if (!changePasswordMutation.isPending) {
      setCurrentPassword("");
      setNewPassword("");
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      onHide();
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header={
        <div className="flex items-center gap-2">
          <Key className="h-5 w-5" style={{ color: "var(--p-primary)" }} />
          <span style={{ color: "var(--p-text-color)" }}>Alterar Senha</span>
        </div>
      }
      style={{ width: "450px" }}
      modal
      dismissableMask={!changePasswordMutation.isPending}
      closable={!changePasswordMutation.isPending}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="currentPassword">Senha Atual</Label>
          <div className="relative">
            <Input
              id="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Digite sua senha atual"
              required
              disabled={changePasswordMutation.isPending}
              className="pr-10"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center hover:opacity-70 transition-opacity"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              disabled={changePasswordMutation.isPending}
              style={{ color: "var(--p-text-muted-color)" }}
            >
              {showCurrentPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <div>
          <Label htmlFor="newPassword">Nova Senha</Label>
          <div className="relative">
            <Input
              id="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Digite sua nova senha (mínimo 8 caracteres)"
              required
              disabled={changePasswordMutation.isPending}
              className="pr-10"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center hover:opacity-70 transition-opacity"
              onClick={() => setShowNewPassword(!showNewPassword)}
              disabled={changePasswordMutation.isPending}
              style={{ color: "var(--p-text-muted-color)" }}
            >
              {showNewPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {newPassword && (
            <div className="mt-2 text-xs">
              {newPassword.length < 8 ? (
                <p className="text-red-500">❌ Mínimo de 8 caracteres</p>
              ) : (
                <p className="text-green-500">✅ Mínimo de 8 caracteres</p>
              )}
              {currentPassword === newPassword && newPassword.length > 0 ? (
                <p className="text-red-500">❌ Deve ser diferente da senha atual</p>
              ) : (
                newPassword.length > 0 && (
                  <p className="text-green-500">✅ Diferente da senha atual</p>
                )
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleHide}
            disabled={changePasswordMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={changePasswordMutation.isPending || !currentPassword || !newPassword}
          >
            {changePasswordMutation.isPending ? "Alterando..." : "Alterar Senha"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

